// Saying why a request failed (2026-10-12). Every refusal — a toast, an inline form error, a dialog — is read through
// here, so it says what the server said in the console's words: the copy for its problem code (`problems.<code>`,
// checked against the contract's list by packages/i18n), with the server's own detail where that names the amounts.
// Only a code with no copy, or a 5xx, gets a generic line, and that one carries the reference to quote to support.

import { describeError, fieldErrorFor } from '@clavian/api-client';
import { problemView, type ProblemView } from '@clavian/i18n';
import { useProblemCopy } from '@clavian/i18n/react';
import { useMemo } from 'react';

export interface ProblemOptions {
  /** The action's own plain line, for a code with no copy of its own and for a 5xx ("Could not save the tag."). */
  readonly fallback?: string;
}

export interface ProblemReader {
  /** The failure as words, with its code, whether it is retryable and the field problems. */
  readonly view: (error: unknown, options?: ProblemOptions) => ProblemView;
  /** The sentence alone, with the reference appended when the answer is the generic one. */
  readonly text: (error: unknown, options?: ProblemOptions) => string;
  /** The message for one request-body field of a validation failure (its `errors[]` entry), if the server named it. */
  readonly fieldMessage: (error: unknown, field: string) => string | undefined;
}

export function useProblem(): ProblemReader {
  const copy = useProblemCopy('console');
  return useMemo<ProblemReader>(() => {
    const view = (error: unknown, options: ProblemOptions = {}): ProblemView => problemView(describeError(error), copy, options);
    return {
      view,
      text: (error, options) => {
        const result = view(error, options);
        const reference = result.reference === undefined ? undefined : copy.line('reference', { id: result.reference });
        return reference === undefined ? result.message : `${result.message} ${reference}`;
      },
      fieldMessage: (error, field) => fieldErrorFor(describeError(error), field)?.message,
    };
  }, [copy]);
}
